import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import api from '../../api/api';
import { Briefcase, Building2, Database, BarChart3, RefreshCw, Inbox, TrendingUp } from 'lucide-react';
import { useTheme } from '../../hooks/useTheme';

import JobListingsTab from '../../components/jobManagement/JobListingsTab';
import EmployersTab from '../../components/jobManagement/EmployersTab';
import ScrapingTab from '../../components/jobManagement/ScrapingTab';
import StatisticsTab from '../../components/jobManagement/StatisticsTab';
import RescrapeTab from '../../components/jobManagement/RescrapeTab';
import EmployerImportsTab from '../../components/jobManagement/EmployerImportsTab';
import EmployerReportingTab from '../../components/jobManagement/EmployerReportingTab';

export default function JobAdminPanel() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const { isDarkMode } = useTheme();

    // Tab management
    const [activeTab, setActiveTab] = useState('jobs'); // jobs, employers, scraping, stats, rescrape
    const [selectedReportEmployer, setSelectedReportEmployer] = useState(null);

    // Jobs state
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(''); // used to prevent multiple fetches while user is typing
    const [filters, setFilters] = useState({
        location: '',
        title: '',
        company: '',
        industry: '',
        timeType: '',
        status: 'active'
    });
    const [debouncedFilters, setDebouncedFilters] = useState({
        location: '',
        title: '',
        company: '',
        industry: '',
        timeType: '',
        status: 'active'
    }); // used to prevent multiple fetches while user is typing free-text filters
    const [dateMode, setDateMode] = useState('all'); // 'custom' | 'all'
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);

    // Employers state — full unpaginated list, shared by Rescrape/Employer Reports and the
    // location-autocomplete datalist (all need every employer, not just one page's worth).
    const [employers, setEmployers] = useState([]);
    const [employersLoading, setEmployersLoading] = useState(false);
    const [industries, setIndustries] = useState([]);
    const [employerStatusFilter, setEmployerStatusFilter] = useState('active');

    // Employers tab table state — paginated (20/page), separate from the full list above.
    const [employerRows, setEmployerRows] = useState([]);
    const [employerRowsLoading, setEmployerRowsLoading] = useState(false);
    const [employerPage, setEmployerPage] = useState(1);
    const [employerTotalPages, setEmployerTotalPages] = useState(1);
    const [employerTotal, setEmployerTotal] = useState(0);
    const [employerSortBy, setEmployerSortBy] = useState(null);
    const [employerSortDir, setEmployerSortDir] = useState('asc');

    // Stats state
    const [stats, setStats] = useState(null);
    const [statsLoading, setStatsLoading] = useState(false);
    const [scrapeHistory, setScrapeHistory] = useState([]);
    const [timeframe, setTimeframe] = useState('overall');

    // Edit modal state (employers)
    const [editingEmployer, setEditingEmployer] = useState(null);
    const [showEmployerEditModal, setShowEmployerEditModal] = useState(false);
    const [savingEmployer, setSavingEmployer] = useState(false);
    const [employerError, setEmployerError] = useState(null);

    // Add Employer state
    const [showAddEmployerModal, setShowAddEmployerModal] = useState(false);
    const [newEmployer, setNewEmployer] = useState({
        Name: '',
        Location: '',
        Industry: '',
        Industries: [],
        CareerSiteURL: '',
        WorkdayURL: ''
    });
    const [addingEmployer, setAddingEmployer] = useState(false);

    // Rescrape state
    const [rescrapingEmployerId, setRescrapingEmployerId] = useState(null);
    const [reloadRates, setReloadRates] = useState([]);
    const [reloadRatesLoading, setReloadRatesLoading] = useState(false);
    const [showSetIntervalModal, setShowSetIntervalModal] = useState(false);
    const [settingInterval, setSettingInterval] = useState(false);
    const [intervalEmployer, setIntervalEmployer] = useState(null);
    const [selectedInterval, setSelectedInterval] = useState('None');
    const [selectedMaxPages, setSelectedMaxPages] = useState();
    const [selectedDay, setSelectedDay] = useState(3);   // 3 = Thursday
    const [selectedHour, setSelectedHour] = useState(20); // 8pm UTC

    // Scraping state
    const [scrapingForm, setScrapingForm] = useState({
        portal_url: '',
        max_pages: 5,
        search_text: '',
        days_back: '',
        fetch_details: false,
        description: false
    });
    const [scrapeResults, setScrapeResults] = useState(null);
    const [scraping, setScraping] = useState(false);
    const [scrapeError, setScrapeError] = useState(null);

    // Excel import state
    const [importLoading, setImportLoading] = useState(false);
    const [importResults, setImportResults] = useState(null);

    // Interval helpers
    const intervalMapping = {
        'None': null,
        'Hourly': 3600,
        'Daily': 86400,
        'Weekly': 604800,
        'Biweekly': 1209600,
        'Monthly': 2592000,
        'Custom Weekly': 'cron_weekly'
    };

    const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

    const secondsToInterval = (seconds) => {
        if (!seconds) return 'None';
        switch (seconds) {
            case 3600: return 'Hourly';
            case 86400: return 'Daily';
            case 604800: return 'Weekly';
            case 1209600: return 'Biweekly';
            case 2592000: return 'Monthly';
            default: return 'None';
        }
    };

    const scheduleToLabel = (schedule) => {
        if (!schedule || !schedule.is_active) return 'None';
        if (schedule.schedule_type === 'cron_weekly' && schedule.schedule_day != null && schedule.schedule_hour != null) {
            const day = DAY_NAMES[schedule.schedule_day] || '?';
            const h = schedule.schedule_hour;
            const ampm = h >= 12 ? 'pm' : 'am';
            const h12 = h % 12 === 0 ? 12 : h % 12;
            return `${day} ${h12}${ampm} UTC`;
        }
        return secondsToInterval(schedule.interval_seconds);
    };

    // ─── Admin guard ────────────────────────────────────────────────────────────
    useEffect(() => {
        if (!user || !user.isAdmin) {
            navigate('/userdashboard');
        }
    }, [user, navigate]);

    // a timer that resets every keystroke, only fetch after the user stops typing for 0.5s
    useEffect(() => {
        const timer = setTimeout(() => {
            setPage(1);
            setDebouncedSearchTerm(searchTerm);
        }, 500);

        return () => clearTimeout(timer);
    }, [searchTerm]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setPage(1);
            setDebouncedFilters(filters);
        }, 500);

        return () => clearTimeout(timer);
    }, [filters]);

    // ─── Fetch helpers ───────────────────────────────────────────────────────────
    const fetchJobs = useCallback(async () => {
        try {
            setLoading(true);
            // Admin tooling shares GET /jobs with the user-facing job browser, which
            // defaults to US-only. Admins need to see international listings too.
            const params = { page, per_page: 20, country: 'all' };
            if (debouncedFilters.location) params.location = debouncedFilters.location;
            if (debouncedFilters.title) params.title = debouncedFilters.title;
            if (debouncedFilters.company) params.company = debouncedFilters.company;
            if (debouncedFilters.industry) params.industry = debouncedFilters.industry;
            if (debouncedFilters.timeType) params.timeType = debouncedFilters.timeType;
            if (debouncedFilters.status) params.status = debouncedFilters.status;
            if (debouncedSearchTerm) params.search = debouncedSearchTerm;

            if (dateMode === 'custom') {
                if (dateFrom) params.date_from = dateFrom;
                if (dateTo) params.date_to = dateTo;
            }
            // 'all' → no date params

            const response = await api.get('/jobs', { params });
            setJobs(response.data.jobs || []);
            setTotalPages(response.data.total_pages || 1);
            setTotal(response.data.total || 0);
        } catch (err) {
            console.error('Error fetching jobs:', err);
        } finally {
            setLoading(false);
        }
    }, [page, debouncedFilters, debouncedSearchTerm, dateMode, dateFrom, dateTo]);

    const fetchEmployers = useCallback(async () => {
        try {
            setEmployersLoading(true);
            const params = { status: employerStatusFilter };
            const response = await api.get('/jobs/employers', { params });
            setEmployers(response.data.employers || []);
        } catch (err) {
            console.error('Error fetching employers:', err);
        } finally {
            setEmployersLoading(false);
        }
    }, [employerStatusFilter]);

    const fetchEmployerPage = useCallback(async () => {
        try {
            setEmployerRowsLoading(true);
            const params = { status: employerStatusFilter, page: employerPage, per_page: 20 };
            if (employerSortBy) {
                params.sort_by = employerSortBy;
                params.sort_dir = employerSortDir;
            }
            const response = await api.get('/jobs/employers', { params });
            setEmployerRows(response.data.employers || []);
            setEmployerTotal(response.data.total || 0);
            const newTotalPages = response.data.total_pages || 1;
            setEmployerTotalPages(newTotalPages);
            if (employerPage > newTotalPages) {
                setEmployerPage(newTotalPages);
            }
        } catch (err) {
            console.error('Error fetching employers page:', err);
        } finally {
            setEmployerRowsLoading(false);
        }
    }, [employerStatusFilter, employerPage, employerSortBy, employerSortDir]);

    const fetchIndustries = useCallback(async () => {
        try {
            const response = await api.get('/jobs/industries');
            setIndustries(response.data.industries || []);
        } catch (err) {
            console.error('Error fetching industries:', err);
        }
    }, []);

    const fetchStats = useCallback(async () => {
        try {
            setStatsLoading(true);
            const response = await api.get('/jobs/stats', { params: { timeframe } });
            setStats(response.data);
        } catch (err) {
            console.error('Error fetching stats:', err);
        } finally {
            setStatsLoading(false);
        }
    }, [timeframe]);

    const fetchScrapeHistory = useCallback(async () => {
        const timeframeToDaysMap = {
            'daily':    1,
            'weekly':   7,
            'biweekly': 14,
            'monthly':  30,
            'yearly':   365,
            'overall':  3650
        };
        const days = timeframeToDaysMap[timeframe] ?? 7;
        try {
            const response = await api.get('/jobs/scrape-history', { params: { days } });
            setScrapeHistory(response.data.history || []);
        } catch (err) {
            console.error('Error fetching scrape history:', err);
        }
    }, [timeframe]);

    const fetchReloadRates = async () => {
        try {
            setReloadRatesLoading(true);
            const response = await api.get('/jobs/reload');
            setReloadRates(response.data.schedules || []);
        } catch (err) {
            console.error('Error fetching reload rates:', err);
            setReloadRates([]);
        } finally {
            setReloadRatesLoading(false);
        }
    };

    // ─── Tab-driven data loading ─────────────────────────────────────────────────
    useEffect(() => {
        if (activeTab === 'jobs') {
            fetchJobs();
        } else if (activeTab === 'employers') {
            fetchEmployerPage();
            if (employers.length === 0) fetchEmployers();
            fetchIndustries();
            fetchReloadRates();
        } else if (activeTab === 'stats') {
            fetchStats();
            fetchScrapeHistory();
        } else if (activeTab === 'rescrape') {
            if (employers.length === 0) fetchEmployers();
            fetchReloadRates();
        } else if (activeTab === 'reporting') {
            if (employers.length === 0) fetchEmployers();
        }
    }, [activeTab, fetchJobs, fetchEmployers, fetchEmployerPage, fetchIndustries, fetchStats, fetchScrapeHistory, employers.length]);

    // Reset to page 1 when the Employers tab's status filter or sort changes
    useEffect(() => {
        setEmployerPage(1);
    }, [employerStatusFilter, employerSortBy, employerSortDir]);

    // Re-fetch stats data when timeframe changes (only on stats tab)
    useEffect(() => {
        if (activeTab === 'stats') {
            fetchStats();
            fetchScrapeHistory();
        }
    }, [timeframe]); // eslint-disable-line react-hooks/exhaustive-deps

    // Reset to page 1 when date filters change
    useEffect(() => {
        setPage(1);
    }, [dateMode, dateFrom, dateTo]);

    // ─── Job handlers ────────────────────────────────────────────────────────────
    const handleDeleteJob = async (jobId) => {
        if (!window.confirm('Are you sure you want to delete this job?')) return;
        try {
            await api.delete(`/jobs/${jobId}`);
            fetchJobs();
        } catch (err) {
            console.error('Error deleting job:', err);
            alert('Failed to delete job');
        }
    };

    // ─── Employer handlers ───────────────────────────────────────────────────────
    const handleEmployerSortChange = (field) => {
        if (employerSortBy === field) {
            setEmployerSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        } else {
            setEmployerSortBy(field);
            setEmployerSortDir('asc');
        }
    };

    const handleEditEmployer = (employer) => {
        const schedule = reloadRates.find(r => r.EmployerID === employer.EmployerID);

        let interval = 'None';
        let rescrapeDay = 3;
        let rescrapeHour = 20;
        if (schedule && schedule.is_active) {
            if (schedule.schedule_type === 'cron_weekly') {
                interval = 'Custom Weekly';
                rescrapeDay = schedule.schedule_day ?? 3;
                rescrapeHour = schedule.schedule_hour ?? 20;
            } else if (schedule.interval_seconds) {
                interval = secondsToInterval(schedule.interval_seconds);
            }
        }

        setEditingEmployer({
            EmployerID: employer.EmployerID,
            Name: employer.Name || '',
            Location: employer.Location || '',
            Industry: employer.IndustryName || '',
            IndustryName: employer.IndustryName || '',
            Industries: (employer.Industries || []).filter(n => n !== (employer.IndustryName || '')),
            CareerSiteURL: employer.CareerSiteURL || '',
            WorkdayURL: employer.WorkdayURL || '',
            RescrapeInterval: interval,
            RescrapeDay: rescrapeDay,
            RescrapeHour: rescrapeHour,
            RetirementThreshold: employer.RetirementThreshold ?? 60
        });
        setShowEmployerEditModal(true);
    };

    const handleSaveEmployer = async () => {
        if (!editingEmployer?.EmployerID) return;
        try {
            setSavingEmployer(true);
            setEmployerError(null);

            await api.put(`/jobs/employers/${editingEmployer.EmployerID}`, {
                Name: editingEmployer.Name,
                Location: editingEmployer.Location,
                Industry: editingEmployer.Industry,
                Industries: editingEmployer.Industries || [],
                CareerSiteURL: editingEmployer.CareerSiteURL || null,
                WorkdayURL: editingEmployer.WorkdayURL || null,
                RetirementThreshold: editingEmployer.RetirementThreshold ?? 60,
            });

            if (editingEmployer.RescrapeInterval === 'None') {
                await api.put(`/jobs/reload/${editingEmployer.EmployerID}`, {
                    is_active: false,
                    interval_seconds: 86400,
                    run_immediately: false
                });
            } else if (editingEmployer.RescrapeInterval === 'Custom Weekly') {
                await api.put(`/jobs/reload/${editingEmployer.EmployerID}`, {
                    schedule_type: 'cron_weekly',
                    schedule_day: editingEmployer.RescrapeDay ?? 3,
                    schedule_hour: editingEmployer.RescrapeHour ?? 20,
                    is_active: true
                });
            } else {
                await api.put(`/jobs/reload/${editingEmployer.EmployerID}`, {
                    schedule_type: 'interval',
                    interval_seconds: intervalMapping[editingEmployer.RescrapeInterval],
                    is_active: true,
                    run_immediately: false
                });
            }

            setShowEmployerEditModal(false);
            setEditingEmployer(null);
            fetchEmployers();
            fetchEmployerPage();
            fetchReloadRates();
        } catch (err) {
            console.error('Error updating employer:', err);
            setEmployerError(err.response?.data?.error || 'Failed to update employer');
        } finally {
            setSavingEmployer(false);
        }
    };

    const handleAddEmployer = async () => {
        try {
            setAddingEmployer(true);
            setEmployerError(null);

            await api.post('/jobs/employers', {
                Name: newEmployer.Name,
                Location: newEmployer.Location,
                Industry: newEmployer.Industry,
                Industries: newEmployer.Industries || [],
                CareerSiteURL: newEmployer.CareerSiteURL || null,
                WorkdayURL: newEmployer.WorkdayURL || null,
            });

            setShowAddEmployerModal(false);
            setNewEmployer({ Name: '', Location: '', Industry: '', Industries: [], CareerSiteURL: '', WorkdayURL: '' });
            fetchEmployers();
            fetchEmployerPage();
        } catch (err) {
            console.error('Error adding employer:', err);
            setEmployerError(err.response?.data?.error || 'Failed to add employer');
        } finally {
            setAddingEmployer(false);
        }
    };

    const handleDeleteEmployer = async (employerId, employerName) => {
        if (!window.confirm(`Are you sure you want to delete ${employerName}? This will also deactivate all associated job listings.`)) return;
        try {
            setEmployerError(null);
            await api.delete(`/jobs/employers/${employerId}`);
            fetchEmployers();
            fetchEmployerPage();
        } catch (err) {
            console.error('Error deleting employer:', err);
            setEmployerError(err.response?.data?.error || 'Failed to delete employer');
        }
    };

    const handleRescrapeEmployer = async (employer) => {
        if (!employer.WorkdayURL) {
            setScrapeError('This employer does not have a Workday portal URL configured.');
            return;
        }
        try {
            setRescrapingEmployerId(employer.EmployerID);
            setScrapeError(null);

            const response = await api.post('/jobs/scrape', {
                portal_url: employer.WorkdayURL,
                employer_id: employer.EmployerID,
                max_pages: 10,
                search_text: ''
            }, { timeout: 10 * 60 * 1000 });

            setScrapeResults(response.data);
            fetchEmployers();
            fetchEmployerPage();
        } catch (err) {
            console.error('Error rescraping employer:', err);
            setScrapeError(err.response?.data?.error || 'Failed to rescrape employer');
        } finally {
            setRescrapingEmployerId(null);
        }
    };

    // ─── Scraping handler ────────────────────────────────────────────────────────
    const handleScrape = async () => {
        if (!scrapingForm.portal_url) {
            setScrapeError('Please enter a portal URL');
            return;
        }
        try {
            setScraping(true);
            setScrapeResults(null);
            setScrapeError(null);

            const payload = {
                portal_url: scrapingForm.portal_url,
                max_pages: scrapingForm.max_pages,
                search_text: scrapingForm.search_text,
                fetch_details: scrapingForm.fetch_details,
                description: scrapingForm.description
            };

            if (scrapingForm.days_back && scrapingForm.days_back > 0) {
                const startDate = new Date();
                startDate.setDate(startDate.getDate() - scrapingForm.days_back);
                payload.start_date = startDate.toISOString().split('T')[0];
            }

            const response = await api.post('/jobs/scrape', payload, { timeout: 10 * 60 * 1000 });
            setScrapeResults(response.data);
        } catch (err) {
            console.error('Error scraping:', err);
            setScrapeError(err.response?.data?.error || err.message || 'Scraping failed');
        } finally {
            setScraping(false);
        }
    };

    // ─── Excel import handlers ───────────────────────────────────────────────────
    const handleDownloadTemplate = async () => {
        try {
            const response = await api.get('/jobs/import/excel/template', { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'PonoPilot_Job_Import_Template.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Error downloading template:', err);
            alert('Failed to download template');
        }
    };

    const handleImportExcel = async (file) => {
        try {
            setImportLoading(true);
            setImportResults(null);
            const formData = new FormData();
            formData.append('file', file);
            const response = await api.post('/jobs/import/excel', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                timeout: 10 * 60 * 1000,
            });
            setImportResults(response.data);
            fetchJobs();
        } catch (err) {
            console.error('Error importing excel:', err);
            setImportResults({
                saved: 0, skipped: 0, errors: 1, total_rows: 0,
                row_results: [{ row: 0, status: 'error', detail: err.response?.data?.error || err.message, enriched: false }]
            });
        } finally {
            setImportLoading(false);
        }
    };

    // ─── Rescrape / schedule handlers ────────────────────────────────────────────
    const handleOpenSetInterval = (employer) => {
        const schedule = reloadRates.find(r => r.EmployerID === employer.EmployerID);

        let interval = 'None';
        if (schedule && schedule.is_active) {
            if (schedule.schedule_type === 'cron_weekly') {
                interval = 'Custom Weekly';
                setSelectedDay(schedule.schedule_day ?? 3);
                setSelectedHour(schedule.schedule_hour ?? 20);
            } else if (schedule.interval_seconds) {
                interval = secondsToInterval(schedule.interval_seconds);
                setSelectedDay(3);
                setSelectedHour(20);
            }
        } else {
            setSelectedDay(3);
            setSelectedHour(20);
        }

        setIntervalEmployer(employer);
        setSelectedInterval(interval);
        setSelectedMaxPages(schedule?.max_pages != null ? schedule.max_pages : 1);
        setShowSetIntervalModal(true);
    };

    const handleSaveInterval = async () => {
        if (!intervalEmployer) return;
        try {
            setSettingInterval(true);
            const maxPagesValue = selectedMaxPages !== '' && selectedMaxPages != null
                ? parseInt(selectedMaxPages)
                : null;

            let payload;
            if (selectedInterval === 'None') {
                payload = { is_active: false, interval_seconds: 86400, run_immediately: false, max_pages: maxPagesValue };
            } else if (selectedInterval === 'Custom Weekly') {
                payload = {
                    schedule_type: 'cron_weekly',
                    schedule_day: selectedDay,
                    schedule_hour: selectedHour,
                    is_active: true,
                    max_pages: maxPagesValue
                };
            } else {
                payload = {
                    schedule_type: 'interval',
                    interval_seconds: intervalMapping[selectedInterval],
                    is_active: true,
                    run_immediately: false,
                    max_pages: maxPagesValue
                };
            }

            await api.put(`/jobs/reload/${intervalEmployer.EmployerID}`, payload);
            setShowSetIntervalModal(false);
            setIntervalEmployer(null);
            fetchReloadRates();
        } catch (err) {
            console.error('Error setting interval:', err);
            alert(`Failed to set interval: ${err.response?.data?.error || err.message}`);
        } finally {
            setSettingInterval(false);
        }
    };

    const handleRunNow = async (employer) => {
        if (!employer.WorkdayURL) {
            alert('This employer does not have a Workday portal URL configured.');
            return;
        }
        if (!window.confirm(`Run scraping immediately for ${employer.Name}?`)) return;
        try {
            setRescrapingEmployerId(employer.EmployerID);
            await api.put(`/jobs/reload/${employer.EmployerID}`, {
                is_active: true,
                interval_seconds: 86400,
                run_immediately: true
            });
            alert(`Scraping scheduled for ${employer.Name}. It will run within the next few minutes.`);
            fetchReloadRates();
        } catch (err) {
            console.error('Error scheduling immediate run:', err);
            alert(`Failed to schedule run: ${err.response?.data?.error || err.message}`);
        } finally {
            setRescrapingEmployerId(null);
        }
    };

    const handleDisableSchedule = async (employer) => {
        if (!window.confirm(`Disable auto-reload for ${employer.Name}?`)) return;
        try {
            await api.put(`/jobs/reload/${employer.EmployerID}`, {
                is_active: false,
                interval_seconds: 86400,
                run_immediately: false
            });
            alert(`Auto-reload disabled for ${employer.Name}`);
            fetchReloadRates();
        } catch (err) {
            console.error('Error disabling schedule:', err);
            alert(`Failed to disable schedule: ${err.response?.data?.error || err.message}`);
        }
    };

    // ─── Derived data ────────────────────────────────────────────────────────────
    const getRescrapeData = () => {
        const reloadMap = new Map(reloadRates.map(r => [r.EmployerID, r]));
        return employers.map(employer => {
            const schedule = reloadMap.get(employer.EmployerID);
            return {
                ...employer,
                interval: scheduleToLabel(schedule),
                next_run_at: schedule?.next_run_at || null,
                last_ran_at: schedule?.last_ran_at || null,
                last_status: schedule?.last_status || null,
                last_error: schedule?.last_error || null,
                is_active: schedule?.is_active || false,
                schedule_type: schedule?.schedule_type || 'interval',
                schedule_day: schedule?.schedule_day ?? null,
                schedule_hour: schedule?.schedule_hour ?? null
            };
        });
    };

    // ─── Render ──────────────────────────────────────────────────────────────────
    return (
        <div className={`min-h-screen ${isDarkMode ? 'bg-surface text-slate-100' : 'bg-surface text-slate-900'}`}>
            <div className="max-w-7xl mx-auto px-4 py-8">
                {/* Header */}
                <div className="mb-8">
                    <button
                        onClick={() => navigate('/admin/dashboard')}
                        className="text-primary-500 hover:text-primary-400 mb-4 text-sm font-medium"
                    >
                        ← Back to Dashboard
                    </button>
                    <h1 className={`text-3xl font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Job Management</h1>
                    <p className={`mt-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Manage job listings, employers, and scraping operations</p>
                </div>

                {/* Tabs */}
                <div className={`rounded-xl border shadow-sm mb-6 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>`}>
                    <div className={`flex border-b ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                        {[
                            { id: 'jobs',      label: 'Job Listings',      icon: Briefcase  },
                            { id: 'employers', label: 'Employers',          icon: Building2  },
                            { id: 'scraping',  label: 'Batch Load',         icon: Database   },
                            { id: 'stats',     label: 'Statistics',         icon: BarChart3  },
                            { id: 'rescrape',  label: 'Reload',             icon: RefreshCw  },
                            { id: 'imports',   label: 'Employer Imports',   icon: Inbox      },
                            { id: 'reporting', label: 'Employer Reports',     icon: TrendingUp }
                        ].map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`flex items-center gap-2 px-6 py-3 font-medium transition-colors ${
                                    activeTab === tab.id
                                        ? 'text-primary-500 border-b-2 border-primary-500'
                                        : isDarkMode ? 'text-slate-400 hover:text-slate-100' : 'text-slate-600 hover:text-slate-900'
                                }`}
                            >
                                <tab.icon size={18} />
                                {tab.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Tab Content */}
                {activeTab === 'jobs' && (
                    <JobListingsTab
                        jobs={jobs}
                        loading={loading}
                        searchTerm={searchTerm}
                        setSearchTerm={setSearchTerm}
                        filters={filters}
                        setFilters={setFilters}
                        setDebouncedFilters={setDebouncedFilters}
                        dateMode={dateMode}
                        setDateMode={setDateMode}
                        dateFrom={dateFrom}
                        setDateFrom={setDateFrom}
                        dateTo={dateTo}
                        setDateTo={setDateTo}
                        page={page}
                        setPage={setPage}
                        totalPages={totalPages}
                        total={total}
                        onDeleteJob={handleDeleteJob}
                    />
                )}

                {activeTab === 'employers' && (
                    <EmployersTab
                        employers={employerRows}
                        employersLoading={employerRowsLoading}
                        allEmployers={employers}
                        employerError={employerError}
                        setEmployerError={setEmployerError}
                        rescrapingEmployerId={rescrapingEmployerId}
                        industries={industries}
                        employerStatusFilter={employerStatusFilter}
                        setEmployerStatusFilter={setEmployerStatusFilter}
                        page={employerPage}
                        setPage={setEmployerPage}
                        totalPages={employerTotalPages}
                        total={employerTotal}
                        sortBy={employerSortBy}
                        sortDir={employerSortDir}
                        onSortChange={handleEmployerSortChange}
                        showAddEmployerModal={showAddEmployerModal}
                        setShowAddEmployerModal={setShowAddEmployerModal}
                        newEmployer={newEmployer}
                        setNewEmployer={setNewEmployer}
                        addingEmployer={addingEmployer}
                        onAddEmployer={handleAddEmployer}
                        showEmployerEditModal={showEmployerEditModal}
                        setShowEmployerEditModal={setShowEmployerEditModal}
                        editingEmployer={editingEmployer}
                        setEditingEmployer={setEditingEmployer}
                        savingEmployer={savingEmployer}
                        onSaveEmployer={handleSaveEmployer}
                        onEditEmployer={handleEditEmployer}
                        onDeleteEmployer={handleDeleteEmployer}
                        onRescrapeEmployer={handleRescrapeEmployer}
                    />
                )}

                {activeTab === 'scraping' && (
                    <ScrapingTab
                        scrapingForm={scrapingForm}
                        setScrapingForm={setScrapingForm}
                        scraping={scraping}
                        scrapeError={scrapeError}
                        setScrapeError={setScrapeError}
                        scrapeResults={scrapeResults}
                        setScrapeResults={setScrapeResults}
                        onScrape={handleScrape}
                        importLoading={importLoading}
                        importResults={importResults}
                        setImportResults={setImportResults}
                        onDownloadTemplate={handleDownloadTemplate}
                        onImportExcel={handleImportExcel}
                    />
                )}

                {activeTab === 'stats' && (
                    <StatisticsTab
                        stats={stats}
                        statsLoading={statsLoading}
                        scrapeHistory={scrapeHistory}
                        timeframe={timeframe}
                        setTimeframe={setTimeframe}
                        onRefresh={() => { fetchStats(); fetchScrapeHistory(); }}
                    />
                )}

                {activeTab === 'rescrape' && (
                    <RescrapeTab
                        rescrapeData={getRescrapeData()}
                        reloadRatesLoading={reloadRatesLoading}
                        rescrapingEmployerId={rescrapingEmployerId}
                        showSetIntervalModal={showSetIntervalModal}
                        setShowSetIntervalModal={setShowSetIntervalModal}
                        intervalEmployer={intervalEmployer}
                        selectedInterval={selectedInterval}
                        setSelectedInterval={setSelectedInterval}
                        selectedDay={selectedDay}
                        setSelectedDay={setSelectedDay}
                        selectedHour={selectedHour}
                        setSelectedHour={setSelectedHour}
                        selectedMaxPages={selectedMaxPages}
                        setSelectedMaxPages={setSelectedMaxPages}
                        settingInterval={settingInterval}
                        onSaveInterval={handleSaveInterval}
                        onOpenSetInterval={handleOpenSetInterval}
                        onRunNow={handleRunNow}
                        onDisableSchedule={handleDisableSchedule}
                        onRefresh={fetchReloadRates}
                    />
                )}

                {activeTab === 'imports' && (
                    <EmployerImportsTab />
                )}

                {activeTab === 'reporting' && (
                    <div className="space-y-5">
                        {/* Employer selector */}
                        <div className={`rounded-xl border shadow-sm p-5 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
                            <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                Select Employer
                            </label>
                            <select
                                value={selectedReportEmployer ?? ''}
                                onChange={e => setSelectedReportEmployer(e.target.value ? Number(e.target.value) : null)}
                                className={`w-full max-w-sm rounded-lg border px-3 py-2 text-sm transition-colors
                                    ${isDarkMode
                                        ? 'bg-slate-700 border-slate-600 text-slate-100'
                                        : 'bg-white border-slate-300 text-slate-900'}`}
                            >
                                <option value="">— Choose an employer —</option>
                                {employers.map(emp => (
                                    <option key={emp.EmployerID} value={emp.EmployerID}>{emp.Name}</option>
                                ))}
                            </select>
                        </div>

                        {/* Report panel */}
                        {selectedReportEmployer ? (
                            <EmployerReportingTab
                                employerId={selectedReportEmployer}
                                isDarkMode={isDarkMode}
                            />
                        ) : (
                            <div className={`rounded-2xl border shadow-sm p-12 text-center ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
                                <TrendingUp className={`mx-auto mb-3 ${isDarkMode ? 'text-slate-600' : 'text-slate-300'}`} size={36} />
                                <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                    Select an employer above to view their report.
                                </p>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}